<template>
  <b-card class="conversion-rate-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body :title="'Stage-wise Conversion Rate Chart'" role="button" tabindex="0">
    <template #header>
      <h6 class="mb-0">Stage-wise Conversion Rate</h6>
    </template>
    <b-card-body>
      <apexchart v-if="chartReady && safeSeries && safeSeries.length" type="line" height="350" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'ConversionRateChart',
  emits: ['chart-point-click'],
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      chartData: [], // raw data for click lookup: [{ from_stage, to_stage, conversion_rate }, ...]
      chartOptions: {
        chart: {
          type: 'line',
          height: 350,
          toolbar: {
            show: false,
          },
          events: {
            dataPointSelection: (e, ctx, config) => this.handleDataPointSelection(e, ctx, config),
            click: (e, ctx, config) => this.handleChartClick(e, ctx, config),
          },
        },
        markers: {
          size: 4,
          hover: { size: 6 },
        },
        stroke: {
          curve: 'smooth',
        },
        xaxis: {
          categories: [], // From Stage - To Stage
        },
        yaxis: {
          title: {
            text: 'Conversion Rate (%)',
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val.toFixed(2) + "% ";
            },
          },
        },
      },
      series: [{
        name: 'Conversion Rate',
        data: [],
      }],
    };
  },
  computed: {
    safeSeries() {
      const s = (this.series || []).filter(Boolean);
      const mapped = s.map((item) => {
        const raw = item && typeof item === 'object' ? item : {};
        return {
          name: raw.name || 'Conversion Rate',
          data: (Array.isArray(raw.data) ? raw.data : []).map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0)),
        };
      });
      return mapped.length ? mapped : [{ name: 'Conversion Rate', data: [] }];
    },
    chartReady() {
      const chart = this.chartOptions && this.chartOptions.chart;
      return chart && typeof chart.height === 'number' && chart.height > 0;
    },
  },
  watch: {
    dashboardData: { deep: true, handler(v) { if (v && v.conversion_rate_chart != null && hasRight(this.user, 'dashboard', 'view')) this.processChartData(v.conversion_rate_chart); } },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.conversion_rate_chart != null) this.processChartData(this.dashboardData.conversion_rate_chart);
          return;
        }
        if (this.dashboardData && this.dashboardData.conversion_rate_chart != null) { this.processChartData(this.dashboardData.conversion_rate_chart); return; }
        this.fetchConversionRateChartData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.conversion_rate_chart != null) this.processChartData(this.dashboardData.conversion_rate_chart);
            return;
          }
          this.fetchConversionRateChartData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [{ name: 'Conversion Rate', data: [] }];
        }
      },
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    async fetchConversionRateChartData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.conversion_rate_chart != null)) {
        if (this.dashboardData && this.dashboardData.conversion_rate_chart != null) this.processChartData(this.dashboardData.conversion_rate_chart);
        return;
      }
      try {
        const response = await this.$http.get('/api/dashboard/crm/conversion-rate-chart', {
          params: this.filters,
        });
        const raw = response.data && response.data.data;
        this.processChartData(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error('Error fetching conversion rate chart data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      if (!data || data.length === 0) {
        this.chartData = [];
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        this.series = [{ name: 'Conversion Rate', data: [] }];
        return;
      }

      this.chartData = data;
      const categories = data.map(item => `${item.from_stage} -> ${item.to_stage}`);
      const seriesData = data.map(item => item.conversion_rate);

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          categories: categories,
        },
      };

      this.series = [{
        name: 'Conversion Rate',
        data: seriesData,
      }];
    },
    emitStageDetail(fromStage, toStage) {
      if (fromStage == null || toStage == null) return;
      const fromEnc = encodeURIComponent(fromStage);
      const toEnc = encodeURIComponent(toStage);
      this.$emit('chart-point-click', `Conversion Rate: ${fromStage} -> ${toStage}`, `/dashboard/crm/conversion-rates/detail/${fromEnc}/${toEnc}`, { from_stage: fromStage, to_stage: toStage });
    },
    handleDataPointSelection(event, chartContext, config) {
      const idx = config && config.dataPointIndex;
      if (idx !== undefined && this.chartData[idx]) {
        const item = this.chartData[idx];
        this.emitStageDetail(item.from_stage, item.to_stage);
      }
    },
    handleChartClick(event, chartContext, config) {
      const idx = config && config.dataPointIndex;
      if (idx !== undefined && this.chartData[idx]) {
        const item = this.chartData[idx];
        this.emitStageDetail(item.from_stage, item.to_stage);
      }
    },
  },
};
</script>

<style scoped>
.conversion-rate-chart {
  margin-top: 20px;
  cursor: pointer; /* Added for clickable affordance */
}
</style>